import { createContext, createElement, useContext } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';

/**
 * 🔴 HOW MUCH ROOM A SUBTREE TAKES — design-system plan DS-9a (the density table, scope §1.2).
 *
 * `packages/shared/tokens/density.json` writes three levels of the density tokens (`--control-h`, `--box-h`,
 * `--control-fs-min`, `--row-py`, `--row-h`, `--field-gap`, `--chip-h`), which the kit's buttons, boxes, rows, fields
 * and filter chips read. The page's own level is set by the token file: the default everywhere, comfortable at the root
 * on a phone (≤640px) or a screen with no hover. `<Density level>` sets a level for its subtree with `data-density`,
 * and says so to React, so what the subtree opens elsewhere follows it:
 *
 * ⚠️ A DIALOG IS DRAWN INTO `<body>`, outside the subtree whose button asked the question. `useConfirm` and `usePrompt`
 * read `useDensity()` where they are called and the dialog carries the level (`<dialog data-density>`) — the till,
 * pinned `default`, must not have its price question grow to 48px under the root's comfortable rule on its tablet.
 * The answer comes from React context, not from the focused element: Safari does not focus a clicked button.
 *
 * Compact is written by the token file and set by no product surface yet (the ledger's per-user choice is later work).
 */
export type DensityLevel = 'compact' | 'default' | 'comfortable';

/** Undefined outside any `<Density>`: the page's level, which the token file decides. */
const DensityContext = /* @__PURE__ */ createContext<DensityLevel | undefined>(undefined);

/** The level the nearest `<Density>` set, or undefined where none did (the root's level applies). */
export function useDensity(): DensityLevel | undefined {
  return useContext(DensityContext);
}

export function Density({
  level,
  as = 'div',
  children,
  ...props
}: HTMLAttributes<HTMLElement> & {
  level: DensityLevel;
  as?: 'div' | 'section' | 'main' | 'span';
  children?: ReactNode;
}) {
  return <DensityContext.Provider value={level}>{createElement(as, { ...props, 'data-density': level }, children)}</DensityContext.Provider>;
}
